/*
 * Copyright (c) Fing Ltd. All rights reserved.
 */

import React, {Component} from 'react';


import {UserContext} from "../UserContext";
import {
    currencySymbol,
    enrichOfferOptions,
    FING_MOBSTATUS_ACTIVE,
    FING_MOBSTATUS_CANCELLED,
    FING_MOBSTATUS_TRIAL, inferMobileBillingCycle, INTERVAL_MONTH, matchesCaseInsensitive, MOBILE_PLATFORM_PLAYSTORE,
    mobilePlatformIcon,
    mobilePlatformPrettyName, productFeatureList,
    productPrettyName, toPrettyPaymentStatus,
    toPrettySubscriptionStatus
} from "../helpers/BillingHelper";
import {formatLongAbsoluteDate} from "../helpers/DateHelper";
import WaitingComponent from "../components/WaitingComponent";
import {Link, withRouter} from "react-router-dom";
import * as DefaultOffers from "./SubscribeDefaultOffers.json";
import {failedToast, successToast} from "../helpers/ToastHelper";
import {ToastContainer} from "react-toastify";

class AccountSubscriptionSummary extends Component {

    static contextType = UserContext;

    constructor(props) {
        super(props);
        this.state = {
            offers: null,
            subscription: null,
            mobileSubscription: null,
            serverError: false,
            showConfirmCancelModal: false,
            showConfirmReactivateModal: false,
            updateSubscriptionInProgress: false,
            manageInProgress: false
        };
    }

    componentDidMount() {
        this.fetchOffers();
    }

    fetchOffers() {
        fetch(`/rest/subscription/offers`)
            .then(response => this.validateRestResponse(response))
            .then(response => response.json())
            .then(result => {
                if (!result && result.options) {
                    enrichOfferOptions(result.options);
                }
                const webSubscription = result && result.currentWebSubscription ? result.currentWebSubscription : undefined;
                const mobileSubscription = result && result.currentMobileSubscription ? result.currentMobileSubscription : undefined;
                this.setState({offers: result, subscription: webSubscription, mobileSubscription: mobileSubscription});
            })
            .catch(e => {
                let defaultOffers = { options: DefaultOffers.options };
                enrichOfferOptions(defaultOffers.options);
                this.setState({offers: defaultOffers});
            });
    }

    fetchPortalUrl(callback) {
        this.setState({manageInProgress: true})
        return fetch('/rest/subscription/manage')
            .then(response => this.validateRestResponse(response))
            .then(response => response.json())
            .then(response => {
                if (response && response.portalSessionUrl) {
                    callback(response.portalSessionUrl); // Leave the "progress" active, until the screen changes
                    return;
                }
                this.setState({manageInProgress: false});
            })
            .catch(ignore => {
                this.setState({manageInProgress: false});
            });
    }

    fetchProfile(userContext) {
        return fetch('/rest/users/profile')
            .then(response => this.validateRestResponse(response))
            .then(response => response.json());
    }

    validateRestResponse(response) {
        if (response.status !== 200) {
            throw Error(response.statusText);
        }
        return response;
    }

    catchRestError(error) {
        console.error('[AccountPaymentMethod] - ', error)
    }

    reactivateSubscription(subscription, callback) {
        const checkoutParams = new URLSearchParams();
        if (subscription.productId) checkoutParams.set("product", subscription.productId.toLowerCase());
        if (subscription.billingCycle) checkoutParams.set("interval", subscription.billingCycle.toLowerCase());
        if (subscription.offerId) checkoutParams.set("discount_offer", subscription.offerId);

        fetch(`/rest/subscription/update?${checkoutParams.toString()}`)
            .then(response => this.validateRestResponse(response))
            .then(response => response.json())
            .then((response) => {
                if (!(response && response.planId)) {
                    callback(null, null);
                    return;
                }

                this.setState({subscription: response});
                callback(response, null);

                // Refresh the user profile if needed
                this.fetchProfile(this.context).then(data => {
                    if (data && data.id && data.accountType) {
                        const newContext = Object.assign({}, this.context);
                        newContext.userProfile.accountType = response.accountType;
                        newContext.userContextUpdater(newContext);
                    }
                });
            })
            .catch(e => callback(null, e))
        ;
    }

    cancelSubscription(callback) {
        fetch('/rest/subscription/remove', {
            method: 'post',
            mode: 'cors',
            credentials: 'same-origin', // send cookies
            headers: {'Content-Type': 'application/json'}
        })
            .then(response => this.validateRestResponse(response))
            .then(response => response.json())
            .then((response) => {
                if (!(response && response.planId)) {
                    callback();
                    return;
                }

                this.setState({subscription: response});
                callback(response);
            })
            .catch(e => callback(null, e));
    }

    // --------------------------------------------------------------------------------

    render() {
        const { serverError, offers, subscription, mobileSubscription } = this.state;

        if (serverError) {
            return <div className="card card-bordered">
                <div className="card-body text-danger">There was an error retrieving your subscription data. Please try refreshing the page.<br />
                    If the problem persists, contact our support team at <a href="mailto:support@fing.com">support@fing.com</a>.</div>
            </div>
        }

        if (!offers) {
            return <div className="card card-bordered">
                <div className="card-body">
                    <WaitingComponent className="my-3" />
                </div>
            </div>
        }

        const hasWebSubscription = subscription && subscription.productId;
        const hasMobileSubscription = mobileSubscription && mobileSubscription.productId && !mobileSubscription.migratedTo;
        const hasMigratedMobileSubscription = mobileSubscription && mobileSubscription.productId && mobileSubscription.migratedTo;

        if (!hasWebSubscription && !hasMobileSubscription) {
            return this.renderChooseYourPlanCard();
        }

        return <>
            {hasMigratedMobileSubscription && !hasWebSubscription && this.renderChooseYourPlanCard()}
            {hasWebSubscription && this.renderWebSubscription(subscription)}
            {(hasMobileSubscription || hasMigratedMobileSubscription) && this.renderMobileSubscription(mobileSubscription)}
            </>;
    }

    renderWebSubscription(subscription) {
        const symbol = currencySymbol(subscription.currency);

        const purchaseDate = subscription.startDate ? formatLongAbsoluteDate(new Date(subscription.startDate).getTime()) : null;
        const endDate = subscription.currentPeriodEnd ? formatLongAbsoluteDate(new Date(subscription.currentPeriodEnd * 1000)) : null;
        const status = subscription.status;
        const paymentStatus = subscription.paymentStatus;
        const paymentOk = subscription.paymentStatus === "succeeded";
        const active = ['TRIALING', 'ACTIVE'].includes(subscription.status) && !subscription.canceledAt;
        const cancelled = ['TRIALING', 'ACTIVE'].includes(subscription.status) && subscription.canceledAt;
        const incomplete = ['INCOMPLETE'].includes(subscription.status);
        const pastDue = ['PAST_DUE'].includes(subscription.status);

        const Row = ({title, value}) => <div className="row align-items-baseline">
            <div className="col-lg-4">
                <label className="input-label mt-0">{title}</label>
            </div>
            <div className="col-lg-8">
                {value}
            </div>
        </div>

        const doRetryOrder = () => {
            this.props.history.push(`/account/subscribe/cart?product=${subscription.productId}&interval=${subscription.billingCycle}`);
        }

        const doOpenPortal = () => {
            this.fetchPortalUrl(portalSessionUrl => {
                window.location.replace(portalSessionUrl);
            });
        }

        const doReactivate = () => {
            this.setState({showConfirmReactivateModal: true});
        }

        const doChangePlan = () => {
            this.props.history.push("/account/subscribe/cart");
        }

        const doCancelPlan = () => {
            this.setState({showConfirmCancelModal: true});
        }

        return <div className="card card-bordered mb-2">
            <div className="card-body">
                <h4>Your subscription</h4>
                <Row title="Product" value={productPrettyName(subscription.productId)} />
                <Row title="Billing" value={matchesCaseInsensitive(subscription.billingCycle, INTERVAL_MONTH) ? "Monthly" : "Yearly"} />
                <Row title="Price" value={subscription.discountAmount ?
                    <span>
                        <span>{symbol &&
                            <span>{symbol}</span>}{subscription.discountAmount || subscription.standardAmount}</span>
                        <del className="text-gray mx-2">{symbol &&
                            <span>{symbol}</span>}{subscription.standardAmount}</del>
                    </span> :
                    <span>{symbol && <span>{symbol}</span>}{subscription.standardAmount}</span>
                }/>
                {status && <Row title="Status" value={<span className={active ? "badge badge-success" : "badge badge-danger"}>
                    {active ? "Active" : cancelled ? "Cancelled" : incomplete ? "Incomplete" : "Expired"}
                </span>} />}
                {purchaseDate && <Row title="Puchased on" value={purchaseDate} />}
                {cancelled && <Row title="Will expire on" value={endDate} />}
                {active && <Row title="Will renew on" value={endDate} />}
                {paymentStatus && !paymentOk && <Row title="Payment" value={<span className="badge badge-warning">
                    {toPrettyPaymentStatus(paymentStatus)}
                </span>} />}

                <div className="mt-3">
                    {
                        incomplete &&
                        <>
                            <button className="btn btn-primary btn-xs" onClick={doRetryOrder}>Retry order</button>
                            <button className="btn btn-outline-secondary btn-xs ml-3" onClick={doOpenPortal} disabled={this.state.manageInProgress === true}>{
                                <>
                                    Finalize pending invoice<span className="fa fa-external-link-alt ml-2"/>
                                    {this.state.manageInProgress &&
                                        <div className="spinner-border spinner-border-sm ml-2" role="status">
                                            <span className="sr-only">Loading...</span>
                                        </div>}
                                </>}
                            </button>
                        </>
                    }
                    {
                        pastDue &&
                        <>
                            <button className="btn btn-primary btn-xs" onClick={doOpenPortal} disabled={this.state.manageInProgress === true}>{
                                <>
                                    Finalize pending invoice<span className="fa fa-external-link-alt ml-2"/>
                                    {this.state.manageInProgress &&
                                        <div className="spinner-border spinner-border-sm ml-2" role="status">
                                            <span className="sr-only">Loading...</span>
                                        </div>}
                                </>}
                            </button>
                        </>
                    }
                    {
                        cancelled && <>
                            <button className="btn btn-primary btn-xs" onClick={doReactivate}>Reactivate</button>
                            <button className="btn btn-outline-secondary btn-xs ml-3" onClick={doOpenPortal} disabled={this.state.manageInProgress === true}>{
                                <>
                                    Payments & Billing<span className="fa fa-external-link-alt ml-2"/>
                                    {this.state.manageInProgress &&
                                        <div className="spinner-border spinner-border-sm ml-2" role="status">
                                            <span className="sr-only">Loading...</span>
                                        </div>}
                                </>}
                            </button>
                        </>
                    }
                    {
                        active && <>
                            <button className="btn btn-primary btn-xs" onClick={doChangePlan}>Change plan</button>
                            <button className="btn btn-outline-secondary btn-xs ml-3" onClick={doOpenPortal} disabled={this.state.manageInProgress === true}>{
                                <>
                                    Payments & Billing<span className="fa fa-external-link-alt ml-2"/>
                                    {this.state.manageInProgress &&
                                        <div className="spinner-border spinner-border-sm ml-2" role="status">
                                            <span className="sr-only">Loading...</span>
                                        </div>}
                                </>}
                            </button>
                            <button className="btn btn-outline-secondary btn-xs ml-3" onClick={doCancelPlan}>Cancel subscription</button>
                        </>
                    }
                </div>
            </div>
            {this.renderCancelSubscriptionModal(subscription)}
            {this.renderReactivateSubscriptionModal(subscription)}
            <ToastContainer />
        </div>
    }
    renderMobileSubscription(mobileSubscription) {
        const purchaseDate = mobileSubscription.purchaseDate ? formatLongAbsoluteDate(new Date(mobileSubscription.purchaseDate).getTime()) : null;
        const endDate = mobileSubscription.periodEndDate ? formatLongAbsoluteDate(new Date(mobileSubscription.periodEndDate).getTime()) : null;
        const status = mobileSubscription.state;
        const willExpire = status === FING_MOBSTATUS_CANCELLED;
        const willRenew = status === FING_MOBSTATUS_TRIAL || status === FING_MOBSTATUS_ACTIVE;
        const migrated = mobileSubscription.migratedTo && mobileSubscription.migratedTo.length > 0;

        const Row = ({title, value}) => <div className="row align-items-baseline">
            <div className="col-lg-4">
                <label className="input-label mt-0">{title}</label>
            </div>
            <div className="col-lg-8">
                {value}
            </div>
        </div>

        return <div className="card card-bordered">
            <div className="card-body">
                <h4><span className={mobilePlatformIcon(mobileSubscription.platform) + " fa-fw mr-2"} />
                    Your {mobilePlatformPrettyName(mobileSubscription.platform)} subscription
                </h4>
                <Row title="Product" value={productPrettyName(mobileSubscription.productId)} />
                {status && <Row title="Status" value={<span className={willRenew ? "badge badge-success" : "badge badge-warning"}>
                    {toPrettySubscriptionStatus(status) ? toPrettySubscriptionStatus(status).label : "-"}
                </span>} />}
                {purchaseDate && <Row title="Puchased on" value={purchaseDate} />}
                {willExpire && <Row title="Will expire on" value={endDate} />}
                {willRenew && <Row title="Will renew on" value={endDate} />}

                {
                    migrated ?
                        <>
                            <a href={mobileSubscription.platform === MOBILE_PLATFORM_PLAYSTORE ?
                                "https://play.google.com/store/account/subscriptions" : "https://apps.apple.com"}
                               className={"btn btn-danger btn-xs mt-3"}>Cancel old subscription on {mobilePlatformPrettyName(mobileSubscription.platform)}<span className="fa fa-external-link-alt ml-2"/></a>

                            <div className="small text-muted mt-2">To review, manage or suspend your mobile
                                subscription, we recommend visiting the {mobilePlatformPrettyName(mobileSubscription.platform)} App on your
                                mobile device.</div>
                        </> :
                        <>
                            <Link to={`/account/subscribe/cart?product=professional&interval=${inferMobileBillingCycle(mobileSubscription.productId).toLowerCase()}`}
                               className="btn btn-primary btn-xs mt-3 mr-3">Migrate to Professional plan</Link>

                            <a href={mobileSubscription.platform === MOBILE_PLATFORM_PLAYSTORE ?
                                "https://play.google.com/store/account/subscriptions" : "https://apps.apple.com"}
                               className={"btn btn-outline-secondary btn-xs mt-3"}>Visit {mobilePlatformPrettyName(mobileSubscription.platform)}<span className="fa fa-external-link-alt ml-2"/></a>

                            <div className="small text-muted mt-2">To update, review, manage or suspend your mobile
                                subscription without migrating it, we recommend visiting the
                                {mobilePlatformPrettyName(mobileSubscription.platform)} App on your mobile device.</div>
                        </>
                }
            </div>
        </div>
    }

    renderChooseYourPlanCard() {
        const {offers} = this.state;
        const cheapest = offers.options.sort((o1, o2) => {
            return (o1.offerAmount || o1.standardAmount) - (o2.offerAmount || o2.standardAmount);
        })[0];
        return (
            <div className="card card-bordered w-100 mb-4 mb-md-0">
                <div className="card-body">
                    <p>You don't have any active subscription.</p>
                    <Link className="btn btn-xs btn-primary text-center transition-3d-hover mr-1" to="/account/subscribe/cart">Choose your
                        plan starting at {cheapest.offerAmount || cheapest.standardAmount}/mo</Link>
                </div>
            </div>
        );
    }

    renderCancelSubscriptionModal(subscription) {
        const { showConfirmCancelModal, updateSubscriptionInProgress } = this.state;

        const handleClose = () => this.setState({ showConfirmCancelModal: false });
        const handleFindCheaperPlan = () => {
            this.setState({showConfirmCancelModal: false});
            this.props.history.push("/account/subscribe/cart?interval=month&product=starter");
        };

        const handleCancelSubscription = () => {
            this.setState({updateSubscriptionInProgress: true});
            this.cancelSubscription((subscription, error) => {
                this.setState({updateSubscriptionInProgress: false, showConfirmCancelModal: false});
                if (subscription && !error) {
                    successToast("Your subscription has been cancelled");
                } else if (error) {
                    failedToast("Your subscription could not be cancelled. Please try again later.");
                }
            });
        };

        const daysLeft = Math.ceil((parseInt(subscription.currentPeriodEnd)*1000 - new Date().getTime()) / (1000 * 60 * 60 * 24));
        let features = productFeatureList(subscription.productId);

        return (
            <div>
                <div className={`modal-backdrop fade ${showConfirmCancelModal ? "show d-block" : "d-none"}`}></div>
                <div className={`modal fade ${showConfirmCancelModal ? "show d-block" : "d-none"}`} tabIndex="-1" role="dialog" aria-labelledby="Cancel_Modal_Label"
                    aria-hidden={!showConfirmCancelModal} aria-modal={showConfirmCancelModal}>
                    <div className="modal-dialog" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title" id="Cancel_Modal_Label">
                                    Cancel your subscription
                            </h5>
                                <button type="button" className="close" onClick={handleClose} data-dismiss="modal" aria-label="Close">
                                    <span aria-hidden="true">×</span><span className="sr-only">Close</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                <p>You're about to cancel your Fing subscription.
                                    You still have <span className="text-dark">{daysLeft} days remaining</span> in your
                                    plan, until <span className="text-dark">{
                                        subscription.currentPeriodEnd && formatLongAbsoluteDate(subscription.currentPeriodEnd)
                                    }</span>. After this date, your account will switch to the Free
                                    plan, <span className="text-highlight-warning">losing access to monitoring features</span>, including:
                                </p>
                                <ul className="list-unstyled text-gray-dark">
                                    {features.map(feature => <li className="pb-1"><i className="fa fa-fw fa-times text-danger mr-3" />{feature}</li>)}
                                </ul>

                                <p className="mt-4">Do you want to proceed with the cancellation?</p>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-sm btn-ghost-danger" onClick={handleCancelSubscription} disabled={updateSubscriptionInProgress === true}>
                                    {updateSubscriptionInProgress ?
                                        <>
                                            <span className="fas fa-times mr-2" />
                                            Cancelling
                                            <div className="spinner-border spinner-border-sm ml-2" role="status">
                                                <span className="sr-only">Loading...</span>
                                            </div>
                                        </>:
                                        <><span className="fas fa-times mr-2" />Proceed to cancel</>
                                    }
                                </button>
                                {
                                    subscription.planId !== "starter_1mo" &&
                                    <button className="btn btn-sm btn-outline-secondary" onClick={handleFindCheaperPlan} disabled={updateSubscriptionInProgress === true}>
                                        <span className="fas fa-tag mr-2" />Lower cost
                                    </button>
                                }
                                <button className="btn btn-sm btn-primary" onClick={handleClose} disabled={updateSubscriptionInProgress === true}>
                                    <span className="fas fa-check mr-2" />Keep subscription
                            </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );

    }

    renderReactivateSubscriptionModal(subscription) {
        const { showConfirmReactivateModal, updateSubscriptionInProgress } = this.state;
        if (!subscription || !subscription.planId) return;

        const handleClose = () => this.setState({ showConfirmReactivateModal: false });
        const handleReactivate = () => {
            this.setState({updateSubscriptionInProgress: true});
            this.reactivateSubscription(subscription, (subscription, error) => {
                this.setState({updateSubscriptionInProgress: false, showConfirmReactivateModal: false});

                if (!subscription && !error) return;
                if (subscription && subscription.planId && !error) {
                    successToast("Your subscription has been reactivated");
                } else if (error) {
                    failedToast("Your subscription could not be reactivated. Please try again later.");
                }
            });
        };

        return <div>
            <div className={`modal-backdrop fade ${showConfirmReactivateModal ? "show d-block" : "d-none"}`}/>
            <div id="confirm_modal" className={`modal fade ${showConfirmReactivateModal ? "show d-block" : "d-none"}`} tabIndex="-1" role="dialog"
                 aria-labelledby="confirm_modal_label" aria-hidden={!showConfirmReactivateModal} aria-modal={showConfirmReactivateModal}>
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3 id="confirm_modal_label" className="modal-title">Confirm reactivation</h3>
                            <button type="button" className="close" onClick={handleClose}>
                                <span aria-hidden="true">×</span><span className="sr-only">Close</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>We are ready to reactivate your current plan.</p>
                            <p>The change will reactivate your subscription and make it immediately available.
                                Any charges or credits will be prorated accordingly and visible in your next invoice.</p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-sm btn-link" onClick={handleClose}>
                                Not now
                            </button>
                            <button className="btn btn-sm btn-primary" onClick={handleReactivate} disabled={updateSubscriptionInProgress === true}>
                                {updateSubscriptionInProgress ?
                                    <>
                                        Updating
                                        <div className="spinner-border spinner-border-sm ml-2" role="status">
                                            <span className="sr-only">Loading...</span>
                                        </div>
                                    </>:
                                    "Reactivate"
                                }
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>;
    }}

export default withRouter(AccountSubscriptionSummary);